class ParallaxImageSection extends HTMLElement {
  connectedCallback() {
    this.fg = this.querySelector('.parallax-image__fg-image');
    if (!this.fg) return;

    // Always-visible policy: never set opacity to 0. The FG image lives at
    // bottom:0 inside a 200svh section, so its NATURAL position is below the
    // viewport during the first half of the scroll — it only appears
    // visually when the section is well past the top of the viewport. This
    // means we get a graceful fallback if JS fails (the image still enters
    // from below naturally), and on mobile where touch-scroll + iOS quirks
    // can suppress scroll events, the image is never invisibly stuck.
    this.fg.style.opacity = '1';

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.onScroll = this.onScroll.bind(this);

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.reducedMotion.addEventListener('change', this.onScroll);
    this.onScroll();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    this.reducedMotion?.removeEventListener('change', this.onScroll);
  }

  onScroll() {
    if (!this.fg) return;
    if (this.reducedMotion?.matches) {
      this.fg.style.transform = '';
      return;
    }

    const rect = this.getBoundingClientRect();
    const vh = window.innerHeight;
    if (rect.bottom < 0 || rect.top > vh) {
      // Section outside the viewport — clear any stale transform but keep
      // the FG visible so it shows up naturally on its next entry.
      this.fg.style.transform = '';
      return;
    }

    // Progress 0 at section entry (rect.top = vh), 1 at sticky end
    // (rect.top = -(rect.height - vh)).
    const progressRaw = (vh - rect.top) / rect.height;
    const progress = Math.max(0, Math.min(1, progressRaw));

    // Don't lift the FG until 50% of the way through the section's scroll.
    const start = 0.5;

    // Past the section's sticky range: clear the transform so the FG sits at
    // its natural CSS position (bottom: 0) and moves out together with the BG.
    if (progressRaw >= 1) {
      this.fg.style.transform = '';
      return;
    }

    if (progress < start) {
      // Before the threshold: clear the transform. The FG is at its natural
      // bottom:0 position, which is below the viewport while the section
      // top is still above viewport center — so it won't visibly peek in.
      this.fg.style.transform = '';
      return;
    }

    // Between threshold and end: lift the FG so it enters from the viewport
    // bottom (at progress = start) and reaches the aligned position
    // (visible top = 0) at progress = 1.
    const adj = (progress - start) / (1 - start);
    const desiredVisibleTop = vh * (1 - adj);
    const naturalTop = rect.top + (rect.height - vh);
    const translateY = desiredVisibleTop - naturalTop;
    this.fg.style.transform = `translate3d(0, ${translateY}px, 0)`;
  }
}

if (!customElements.get('parallax-image-section')) {
  customElements.define('parallax-image-section', ParallaxImageSection);
}
